import { ExamAccess, InActiveStudentAcess, ReRegAccess } from './CustomModels';

/**
 * Top-level sidebar routes where a forced redirect is allowed to fire (see isMainRoute).
 * Nested sub-pages (a course, an assessment, a ticket detail, ...) are deliberately left
 * out: a student can reach one directly in a new tab (e.g. "View Assessment"), which is a
 * fresh app mount that re-checks these conditions, so leaving a sub-page out of this list
 * stops that fresh check from redirecting them away from what they opened. These must match
 * currentPath exactly - some (e.g. /mycourses) are also the literal start of an unrelated
 * sub-route (/mycourses/:qid), so a prefix match here would wrongly treat that sub-route as main.
 */
const EXACT_MAIN_ROUTES = [
    '/home',
    '/gdc',
    '/calendar',
    '/profile',
    '/mycourses',
    '/mytickets',
    '/myexam',
    '/status',
    '/reregStatus',
    '/ReRegUpcomming',
    '/examregistration',
    '/resultWithheld',
    '/complimentcomments',
    '/myassessments',
    '/requiredStudentDetails',
    '/settings',
    '/newchat',
];

/** Main routes whose own sub-paths (tabs, an id) are still the same page, so a prefix match is safe */
const MAIN_ROUTE_PREFIXES = ['/mynotifications', '/student-hub'];

/**
 * Checks if a path is a main route (vs. a nested sub-page like a course or assessment),
 * which is where forced redirects are allowed to fire.
 * @param {string} currentPath Pathname of the route being guarded
 * @returns {boolean} Whether currentPath is a main route
 */
function isMainRoute(currentPath) {
    return (
        EXACT_MAIN_ROUTES.includes(currentPath) ||
        MAIN_ROUTE_PREFIXES.some((route) => currentPath === route || currentPath?.startsWith(`${route}/`))
    );
}

const FORCED_REDIRECT_THROTTLE_MS = 30 * 1000;

/** Reads/lazily-initializes the never-cleared, per-tab record of each target's last redirect */
function getForcedRedirectHistory() {
    window.forcedRedirectLastAttemptAt = window.forcedRedirectLastAttemptAt || {};
    return window.forcedRedirectLastAttemptAt;
}

/**
 * Returns target, unless it was already redirected to within FORCED_REDIRECT_THROTTLE_MS,
 * in which case null so the caller falls through to the next-priority check.
 * @param {string} target Forced-redirect path being considered
 * @returns {string|null} target, or null if throttled
 */
function resolveRedirect(target) {
    const history = getForcedRedirectHistory();
    if (history[target] && Date.now() - history[target] < FORCED_REDIRECT_THROTTLE_MS) {
        return null;
    }
    history[target] = Date.now();
    return target;
}

/**
 * Records a forced redirect made outside getForcedRedirectTarget (e.g. LoginForm's own
 * post-login redirect), so the throttle above also covers it.
 * @param {string} target Forced-redirect path that was just navigated to
 */
export function recordForcedRedirect(target) {
    getForcedRedirectHistory()[target] = Date.now();
}

/**
 * Centralized precedence for forced (student-can't-avoid) navigation. A student can
 * qualify for more than one of these at once (e.g. missing required details AND an
 * open re-registration), so this is the single source of truth for which forced page
 * wins, highest priority first:
 *
 *   1. On Hold                  -> /status
 *   2. Open re-registration     -> /reregStatus   (open articulation OR open re-reg item)
 *   3. Missing required details -> /requiredStudentDetails
 *   4. PMC re-reg soft launch   -> /ReRegUpcomming
 *   5. Open exam registration   -> /examregistration
 *   6. Results withheld         -> /ResultWithheld
 *
 * On Hold is an account-level lock, so it overrides everything. Open re-registration/
 * articulation is time-sensitive and has always taken precedence, so it stays above
 * required student details. Required student details is a lightweight one-time
 * onboarding step that shouldn't be starved by the lower-priority nags below it.
 *
 * None of this fires unless currentPath is a main route (see isMainRoute) - a student on a
 * sub-page like a course or assessment is never force-redirected away from it.
 *
 * Each target is also throttled (see resolveRedirect): once redirected-to, it won't fire again
 * for FORCED_REDIRECT_THROTTLE_MS - a throttled reason is skipped in favor of the next one.
 *
 * @param {object} params
 * @param {string} params.currentPath Pathname of the route being guarded
 * @param {object} params.user User record from LoginCheckService (res?.data)
 * @param {boolean} params.hasOpenReReg Open articulation or open re-registration item exists
 * @param {boolean} params.hasMissingDetails Student is missing required details (see hasRequiredStudentDetails)
 * @param {boolean} params.hasPmcSoftLaunch PMC re-registration soft launch applies
 * @param {boolean} params.hasOpenExamReg Open exam registration exists
 * @param {boolean} params.isResultsWithheld Results are withheld and not yet acknowledged
 * @returns {string|null} The path to force-navigate to, or null to let the requested route render
 */
export function getForcedRedirectTarget({
    currentPath,
    user,
    hasOpenReReg,
    hasMissingDetails,
    hasPmcSoftLaunch,
    hasOpenExamReg,
    isResultsWithheld,
}) {
    if (!isMainRoute(currentPath)) {
        return null;
    }

    const onAllowedPath = (allowList) => allowList.some((path) => currentPath?.includes(path));

    if (user?.status === 'On Hold') {
        if (onAllowedPath(InActiveStudentAcess)) {
            return null;
        }
        const target = resolveRedirect('/status');
        if (target) {
            return target;
        }
    }
    if (hasOpenReReg) {
        if (onAllowedPath(ReRegAccess)) {
            return null;
        }
        const target = resolveRedirect('/reregStatus');
        if (target) {
            return target;
        }
    }
    if (hasMissingDetails) {
        if (currentPath?.includes('requiredStudentDetails')) {
            return null;
        }
        const target = resolveRedirect('/requiredStudentDetails');
        if (target) {
            return target;
        }
    }
    if (hasPmcSoftLaunch) {
        if (currentPath?.includes('ReRegUpcomming')) {
            return null;
        }
        const target = resolveRedirect('/ReRegUpcomming');
        if (target) {
            return target;
        }
    }
    if (hasOpenExamReg) {
        if (onAllowedPath(ExamAccess)) {
            return null;
        }
        const target = resolveRedirect('/examregistration');
        if (target) {
            return target;
        }
    }
    if (isResultsWithheld) {
        if (currentPath?.includes('ResultWithheld')) {
            return null;
        }
        const target = resolveRedirect('/ResultWithheld');
        if (target) {
            return target;
        }
    }
    return null;
}
